import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  Input,
  OnDestroy,
  OnInit,
  TemplateRef,
  ViewChild,
  ViewContainerRef,
  ViewEncapsulation
} from '@angular/core';
import { DomSanitizer, SafeResourceUrl } from '@angular/platform-browser';
import { Subject } from 'rxjs';
import { delayWhen, filter, takeUntil } from 'rxjs/operators';

import { COMMON_EVENTS, LABEL_EVENTS, SHAPE_EVENTS, TABLE_EVENTS } from './label.event';
import { LabelHub } from './label.hub';
import { IMagnifierChangeEvent, IMouseEnterLabelsEvent, ISafeAny } from './label.type';
import { logger } from './label.util';

@Component({
  selector: 'bixi-label',
  template: `
  <div
    class="bixi-label"
    nz-popover
    [(nzPopoverVisible)]="magnifierVisible"
    [nzPopoverTrigger]="null"
    [nzPopoverContent]="magnifier"
  >
    <iframe
      #iframe
      [src]="url"
      (load)="onLoad()"
      [attr.id]="hub.id"
      [title]="hub.id">
    </iframe>
    <bixi-label-tooltip
      [tooltipVisible]="tooltipVisible"
      [tooltip]="mouseEnterLabelsEvent"
      [content]="labelTooltipContent">
    </bixi-label-tooltip>
    <ng-template #magnifier>
      <div [ngStyle]="{
        width: magnifierOptions.width + 'px',
        height: magnifierOptions.height + 'px',
        position: 'relative',
        overflow: 'hidden'
      }">
        <img
          *ngIf="magnifierOptions"
          [attr.src]="magnifierOptions.src"
          [attr.width]="magnifierOptions.viewportWidth"
          [attr.height]="magnifierOptions.viewportHeight"
          [ngStyle]="{
            position: 'absolute',
            transform: 'translate(' + -magnifierOptions.left + 'px, ' + -magnifierOptions.top + 'px)'
          }"
        />
      </div>
    </ng-template>
   </div>
  `,
  exportAs: 'bixiLabel',
  encapsulation: ViewEncapsulation.None,
  preserveWhitespaces: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  // TODO 做主题切换
  styleUrls: ['./style/index.less']
})
export class BixiLabelComponent implements OnInit, OnDestroy {
  _engine: string;
  url: SafeResourceUrl;
  mouseEnterLabelsEvent: IMouseEnterLabelsEvent | null = null;
  tooltipVisible = false;
  magnifierVisible = false;
  magnifierOptions: IMagnifierChangeEvent | null = null

  @Input() dragBoundary: string;
  @Input() hub: LabelHub;
  @Input()
  set engine(val: string) {
    this.url = this.sanitizer.bypassSecurityTrustResourceUrl(`${val || './bixi-label/index.html'}`);
    this._engine = val;
  }
  get engine() {
    return this._engine;
  }
  @Input() labelTooltipContent: TemplateRef<ISafeAny> | undefined;
  @Input() labelTooltipDisabled = false;

  @ViewChild('iframe', { read: ViewContainerRef }) iframe: ViewContainerRef;

  get api() {
    if (!this.iframe) return;
    return this.iframe.element.nativeElement.contentWindow.BIXI_LABEL_API;
  }

  private destroy$ = new Subject<void>();

  constructor(
    public sanitizer: DomSanitizer,
    private cdr: ChangeDetectorRef
  ) { }

  onLoad() {
    if (!this.hub) return;
    const api = this.api;
    this.hub.setApi(api);
    this.hub.loadedState$.next(!!api);
  }

  ngOnInit() {
    this.hub.eventsAfterLoaded$
      .pipe(
        filter(i => i !== null),
        delayWhen(() => this.hub.loaded$),
        takeUntil(this.destroy$)
      )
      .subscribe(params => {
        const { method, args } = params;
        this.api[method](args);
      });
    this.hub.eventsAfterReady$
      .pipe(
        filter(i => i !== null),
        delayWhen(() => this.hub.loaded$),
        takeUntil(this.destroy$)
      )
      .subscribe(params => {
        const { method, args } = params;
        this.api[method](args);
      });
    window.addEventListener('message', this.messageHandler);
  }

  messageHandler = (e: MessageEvent) => {
    const { id, event, data } = e.data;
    if (this.hub.id !== id) return;
    switch (event) {
      // +++++++++ COMMON +++++++++++++
      case COMMON_EVENTS.ready: {
        this.hub.pageCount$.next(data.pageCount);
        this.hub.readyState$.next(true);
        break;
      }
      case COMMON_EVENTS.page_number_change: {
        if (this.hub.pageNumber === data.pageNumber) return;
        this.hub.pageNumber$.next(data.pageNumber);
        break;
      }
      case COMMON_EVENTS.history_change: {
        this.hub.historyChange$.next(data);
        break;
      }
      case COMMON_EVENTS.magnifier_change: {
        this.hub.magnifierChange$.next(data);
        this.magnifierVisible = !!data;
        this.magnifierOptions = data;
        this.cdr.detectChanges();
        break;
      }
      case COMMON_EVENTS.viewports_change: {
        this.hub.viewportsChange$.next(data);
        break;
      }
      case COMMON_EVENTS.pdf_scrolling: {
        if (this.mouseEnterLabelsEvent) {
          this.mouseEnterLabelsEvent = null;
          this.tooltipVisible = false;
          this.cdr!.detectChanges();
        }
        this.hub.pdfScrolling$.next(data);
        break;
      }
      case COMMON_EVENTS.pdf_load_failed: {
        this.hub.pdfLoadFailed$.next();
        break;
      }
      case COMMON_EVENTS.keydown: {
        const _p = {
          key: data.key,
          code: data.code,
          location: data.location,
          ctrlKey: data.ctrlKey,
          shiftKey: data.shiftKey,
          altKey: data.altKey,
          metaKey: data.metaKey,
          repeat: data.repeat,
          isComposing: data.isComposing,
          charCode: data.charCode,
          keyCode: data.keyCode,
          bubbles: true,
          cancelable: true
        };
        document.body.dispatchEvent(new KeyboardEvent('keydown', _p));
        break;
      }
      // +++++++++ LABEL +++++++++++++
      case LABEL_EVENTS.create_labels: {
        this.hub.createLabels$.next(data);
        break;
      }
      case LABEL_EVENTS.delete_labels: {
        this.hub.deleteLabels$.next(data);
        break;
      }
      case LABEL_EVENTS.resize_labels: {
        this.hub.resizeLabels$.next(data);
        break;
      }
      case LABEL_EVENTS.drag_labels: {
        this.hub.dragLabels$.next(data);
        break;
      }
      case LABEL_EVENTS.click_labels: {
        this.hub.clickLabels$.next(data);
        break;
      }
      case LABEL_EVENTS.mouse_enter_labels: {
        this.mouseEnterLabelsEvent = data as IMouseEnterLabelsEvent;
        this.tooltipVisible = !this.labelTooltipDisabled && Boolean(this.mouseEnterLabelsEvent.data.find(e => !e.tooltipHidden));
        this.cdr.detectChanges();
        this.hub.mouseEnterLabels$.next(data);
        break;
      }
      case LABEL_EVENTS.mouse_leave_labels: {
        this.mouseEnterLabelsEvent = null;
        this.tooltipVisible = false;
        this.cdr.detectChanges();
        this.hub.mouseLeaveLabels$.next(data);
        break;
      }

      // +++++++++ TABLE +++++++++++++
      case TABLE_EVENTS.view_tables_change: {
        this.hub.viewTablesChange$.next(data);
        break;
      }
      case TABLE_EVENTS.create_tables: {
        this.hub.createTables$.next(data);
        break;
      }
      case TABLE_EVENTS.delete_tables: {
        this.hub.deleteTables$.next(data);
        break;
      }
      case TABLE_EVENTS.click_tables: {
        this.hub.clickTables$.next(data);
        break;
      }
      case TABLE_EVENTS.mouse_enter_tables: {
        this.hub.mouseEnterTables$.next(data);
        break;
      }
      case TABLE_EVENTS.mouse_leave_tables: {
        this.hub.mouseLeaveTables$.next(data);
        break;
      }
      case TABLE_EVENTS.update_tables: {
        this.hub.updateTables$.next(data);
        break;
      }
      case TABLE_EVENTS.merge_tables: {
        this.hub.mergeTables$.next(data);
        break;
      }
      case TABLE_EVENTS.split_tables: {
        this.hub.splitTables$.next(data);
        break;
      }

      // +++++++++ RT TABLE +++++++++++++
      case TABLE_EVENTS.update_ir_tables: {
        this.hub.updateIRTables$.next(data);
        break;
      }
      case TABLE_EVENTS.create_ir_table_cells: {
        this.hub.createIRTableCells$.next(data);
        break;
      }

      // +++++++++ SHAPE +++++++++++++
      case SHAPE_EVENTS.create_shapes: {
        this.hub.createShapes$.next(data);
        break;
      }
      case SHAPE_EVENTS.click_shapes: {
        this.hub.clickShapes$.next(data);
        break;
      }
      case SHAPE_EVENTS.update_shapes: {
        this.hub.updateShapes$.next(data);
        break;
      }
      case SHAPE_EVENTS.delete_shapes: {
        this.hub.deleteShapes$.next(data);
        break;
      }
      case SHAPE_EVENTS.doms_change: {
        this.hub.shapeDomsChange$.next(data);
        break;
      }

      default: {
        if (this.hub.dev) {
          logger.warn(`[@bixi/label] Unhandled Event(${this.hub.id}) `, event, data);
        }
        break;
      }
    }
  }

  ngOnDestroy() {
    this.hub.destroy();
    window.removeEventListener('message', this.messageHandler);
    this.destroy$.next();
    this.destroy$.complete();
  }
}
